import { useEffect, useState } from "react";
import { api, errMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GraduationCap, Plus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { FilterPanel } from "@/components/admin/FilterPanel";
import { BulkActionsBar } from "@/components/admin/BulkActionsBar";
import { useFilters } from "@/lib/useFilters";
import { useSelection } from "@/lib/useSelection";

const CE_FILTERS = [
  { key: "search", type: "search", label: "Search activity or provider", paths: ["title", "provider"] },
  { key: "verified", type: "boolean", label: "Verified", path: "verified" },
  { key: "category", type: "select", label: "Category", path: "category",
    options: [{ value: "general", label: "General" }, { value: "ethics", label: "Ethics" }, { value: "clinical", label: "Clinical" }] },
  { key: "date_from", type: "dateFrom", label: "From", path: "completion_date" },
  { key: "date_to", type: "dateTo", label: "To", path: "completion_date" },
];

export default function CERecords() {
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState({ title: "", provider: "", hours: "", completion_date: "", category: "general" });
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    const { data } = await api.get("/ce-records");
    setRecords(data);
  };
  useEffect(() => { reload(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.title || !form.provider || !form.hours || !form.completion_date) {
      toast.error("Fill all fields"); return;
    }
    setBusy(true);
    try {
      await api.post("/ce-records", { ...form, hours: Number(form.hours) });
      toast.success("CE record added");
      setForm({ title: "", provider: "", hours: "", completion_date: "", category: "general" });
      reload();
    } catch (e) { toast.error(errMessage(e)); } finally { setBusy(false); }
  };

  const verify = async (id) => {
    try { await api.patch(`/ce-records/${id}/verify`); toast.success("Verified"); reload(); }
    catch (e) { toast.error(errMessage(e)); }
  };

  const totalHours = records.reduce((s, r) => s + (r.hours || 0), 0);
  const verifiedHours = records.filter((r) => r.verified).reduce((s, r) => s + (r.hours || 0), 0);
  const filters = useFilters(records, CE_FILTERS, { defaultSort: { field: "completion_date", dir: "desc" } });
  const selection = useSelection(filters.filtered);
  const isAdmin = user.role === "admin";

  return (
    <div className="animate-fade-up" data-testid="ce-records-page">
      <div className="mb-6 flex flex-wrap justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">IBTHM · Continuing education</div>
          <h1 className="font-heading text-3xl font-bold text-primary flex items-center gap-3"><GraduationCap className="h-7 w-7" />CE Records</h1>
          <p className="text-sm text-slate-500 mt-1">Track continuing education hours toward credential renewal.</p>
        </div>
        <Card className="px-5 py-3 border-slate-200">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Total hours</div>
          <div className="font-heading text-2xl font-bold text-primary">{totalHours.toFixed(1)}</div>
          <div className="text-xs text-emerald-700 font-medium">{verifiedHours.toFixed(1)} verified</div>
        </Card>
      </div>

      {(user.role === "practitioner" || user.role === "applicant") && (
        <Card className="p-5 border-slate-200 bg-slate-50 mb-5">
          <h2 className="font-heading text-sm font-semibold text-primary mb-3">Log a new CE activity</h2>
          <div className="grid md:grid-cols-12 gap-3">
            <div className="md:col-span-4">
              <Label className="mb-1.5 block text-xs">Activity title</Label>
              <Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="ce-title" />
            </div>
            <div className="md:col-span-3">
              <Label className="mb-1.5 block text-xs">Provider</Label>
              <Input value={form.provider} onChange={(e) => set("provider", e.target.value)} data-testid="ce-provider" />
            </div>
            <div className="md:col-span-2">
              <Label className="mb-1.5 block text-xs">Hours</Label>
              <Input type="number" step="0.5" value={form.hours} onChange={(e) => set("hours", e.target.value)} data-testid="ce-hours" />
            </div>
            <div className="md:col-span-2">
              <Label className="mb-1.5 block text-xs">Date</Label>
              <Input type="date" value={form.completion_date} onChange={(e) => set("completion_date", e.target.value)} data-testid="ce-date" />
            </div>
            <div className="md:col-span-1 flex items-end">
              <Button onClick={submit} disabled={busy} className="w-full bg-primary text-white hover:bg-[#113A64]" data-testid="ce-add">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>
      )}

      <FilterPanel
        schemas={CE_FILTERS} values={filters.values} set={filters.set} clear={filters.clear}
        count={filters.count} total={records.length} activeCount={filters.activeCount}
        sortFields={[
          { key: "completion_date", label: "Completion date" },
          { key: "hours", label: "Hours" },
          { key: "title", label: "Activity title" },
        ]}
        sort={filters.sort} setSort={filters.setSort} testid="ce-filters"
      />

      {isAdmin && <BulkActionsBar collection="ce_records" selection={selection} onDone={reload} />}

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
              <tr>
                {isAdmin && <th className="p-4 w-10"><Checkbox checked={selection.allSelected} onCheckedChange={selection.toggleAll} data-testid="ce-select-all" /></th>}
                <th className="p-4">Activity</th>
                <th className="p-4">Provider</th>
                <th className="p-4">Hours</th>
                <th className="p-4">Date</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filters.filtered.length === 0 && <tr><td colSpan={isAdmin ? 7 : 6} className="p-10 text-center text-slate-500">No CE records match the current filters</td></tr>}
              {filters.filtered.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  {isAdmin && <td className="p-4"><Checkbox checked={selection.isSelected(r.id)} onCheckedChange={() => selection.toggle(r.id)} data-testid={`ce-select-${r.id}`} /></td>}
                  <td className="p-4 font-medium text-primary">{r.title}</td>
                  <td className="p-4 text-xs text-slate-700">{r.provider}</td>
                  <td className="p-4 text-xs font-mono-data text-primary">{r.hours}</td>
                  <td className="p-4 text-xs text-slate-500">{r.completion_date}</td>
                  <td className="p-4">
                    {r.verified
                      ? <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">Verified</Badge>
                      : <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">Unverified</Badge>}
                  </td>
                  <td className="p-4 text-right">
                    {(user.role === "admin" || user.role === "reviewer") && !r.verified && (
                      <Button size="sm" variant="outline" onClick={() => verify(r.id)} data-testid={`ce-verify-${r.id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Verify
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
